/* eslint-disable no-shadow */
/* eslint-disable import/no-cycle */
import {
  ActionTree, Module, MutationTree, GetterTree
} from 'vuex';

import IInitialSettingsViewModel from '@/models/interfaces/IInitialSettingsViewModel';
import VehicleType from '@/enums/VehicleType';
import ValuationInitialSettings from '@/models/valuation/ValuationInitialSettings';
import { initialSettingsService } from '@/services/InitialSettingsService';
import { EventTypeEnum, TrackingService } from '@/services/TrackingService';
import { PublisherService } from '@/services/PublisherService';
import ExtraData from '@/models/ExtraData';
import InitialSettingState from '../states/initial-settings-state';
import IInitialSettingState from '../states/interfaces/initial-settings-state.d';
import IRootState from '../states/interfaces/index.d';

export const state: IInitialSettingState = new InitialSettingState();

export const getters: GetterTree<IInitialSettingState, IRootState> = {
  initialSettings: (state) => state.initialSettings,
  valuationInitialSettings: (state) => state.valuationInitialSettings,
  correlationId: (state) => state.initialSettings.correlationId,
  leadSourceGuid: (state) => state.initialSettings.leadSourceGuid,
  thanksPageId: (state) => state.initialSettings.thanksPageId,
  thanksEmailTemplateName: (state) => state.initialSettings.thanksEmailTemplateName,
  vehicleTypeName: (state) => {
    if (state.initialSettings.vehicleType === VehicleType.Car) {
      return 'car';
    }
    return 'van';
  },
  hideDerivativeOptions: (state) => state.initialSettings.hideDerivativeOptions,
  allowFullNavigation: (state) => state.initialSettings.allowFullNavigation,
  showErrorsOnNext: (state) => state.initialSettings.showErrorsOnNext,
  hideSlidersOnTouchScreens: (state) => state.initialSettings.hideSlidersOnTouchScreens,
  showExitIntentSurvey: (state) => state.initialSettings.showExitIntentSurvey,
  exitIntentSurveyDelay: (state) => state.initialSettings.exitIntentSurveyDelay,
  exitIntentHeaderCopy: (state) => state.initialSettings.exitIntentHeaderCopy,
  exitIntentPlaceholderCopy: (state) => state.initialSettings.exitIntentPlaceholderCopy,
  exitIntentCTACopy: (state) => state.initialSettings.exitIntentCTACopy,
  useImaginStudio: (state) => state.initialSettings.useImaginStudio,
  imaginStudioZoomLevel: (state) => state.initialSettings.imaginStudioZoomLevel,
  hideFieldsWithOneOption: (state) => state.initialSettings.hideFieldsWithOneOption,
  useCapImagesRangeIds: (state) => state.initialSettings.useCapImagesRangeIds
};

export const mutations: MutationTree<IInitialSettingState> = {
  setInitialSettings: (state, initialSettings: IInitialSettingsViewModel) => {
    state.initialSettings = initialSettings;
  },
  setValuationInitialSettings: (state, valuationInitialSettings: ValuationInitialSettings) => {
    state.valuationInitialSettings = valuationInitialSettings;
  }
};

export const actions: ActionTree<IInitialSettingState, IRootState> = {
  submitInitialSettings(
    { commit, dispatch, rootGetters },
    initialSettings: IInitialSettingsViewModel
  ) {
    const tlaBodyStyles = initialSettingsService.tlaBodyStyleQueryString();
    const tlaFuelTypes = initialSettingsService.tlaFuelTypeQueryString();

    if (tlaBodyStyles && tlaBodyStyles.length > 0) {
      initialSettings.bodystyles = tlaBodyStyles;
    }

    if (tlaFuelTypes && tlaFuelTypes.length > 0) {
      initialSettings.fuelTypes = tlaFuelTypes;
    }

    const existingCorrelationId = initialSettingsService.hasCorrelationId();
    initialSettings.correlationId = initialSettingsService.getOrGenerateCorrelationId();
    dispatch('enquiryModule/submitExtraData', new ExtraData('correlationId', initialSettings.correlationId), { root: true });
    commit('setInitialSettings', initialSettings);

    dispatch('vehicleModule/submitVehicleType', initialSettings.vehicleType, { root: true });
    dispatch('vehicleModule/submitVehicleLevel', initialSettings.vehicleLevel, { root: true });

    dispatch('searchQueryModule/submitVehicleType', initialSettings.vehicleType, { root: true });
    dispatch('searchQueryModule/submitVehicleLevel', initialSettings.vehicleLevel, { root: true });
    dispatch('searchQueryModule/submitCampaignVehicleLevel', initialSettings.campaignVehicleLevel, { root: true });

    if (initialSettings.bodystyles !== null) {
      dispatch('searchQueryModule/submitBodystyles', initialSettings.bodystyles, { root: true });
    }

    if (initialSettings.fuelTypes !== null) {
      dispatch('searchQueryModule/submitFuelTypes', initialSettings.fuelTypes, { root: true });
    }

    dispatch('policyModule/submitPolicyData', initialSettings, { root: true });

    // Only send this load event if we've just created a new correlation id
    // otherwise we might be sending duplicate for a page refresh
    if (!existingCorrelationId) {
      TrackingService.instance.sendEvent(
        EventTypeEnum.PageLoad,
        rootGetters['initialSettingsModule/correlationId'],
        {
          screenWidth: window.screen.width,
          screenHeight: window.screen.height,
          fullUrl: window.location.href,
          referrer: document.referrer,
          publisher: PublisherService.publisherName,
          vehicleType: initialSettings.vehicleType,
          manufacturerId: initialSettings.manufacturers ? initialSettings.manufacturers[0] : null,
          rangeId: initialSettings.range
        }
      );
    }
  },
  submitValuationInitialSettings(
    { commit },
    valuationInitialSettings: ValuationInitialSettings
  ) {
    commit('setValuationInitialSettings', valuationInitialSettings);
  }
};

export const initialSettingsModule: Module<IInitialSettingState, IRootState> = {
  state,
  actions,
  mutations,
  getters,
  namespaced: true
};
